import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from './context/AuthContext';
import { useProperty } from './context/PropertyContext';
import { useSubscription } from './context/SubscriptionContext';
import Notification from './components/Notification';
import Spinner from './components/Spinner';
import LimitReachedModal from './components/LimitReachedModal';
import AdvancedROIMetrics from './components/AdvancedROIMetrics';
import PropertyComparison from './components/PropertyComparison';
import PDFExportButton from './components/PDFReport';
import { fetchPropertyData, recalculateROI } from './services/propertyService';
import { saveUserPropertyData } from './services/savePropertyService';
import './styles/FormPage.css';

const FormPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updatePropertyData, updateRoiResults } = useProperty();
  const { subscription, canPerformAction, trackPropertyAnalysis, upgradeSubscription } = useSubscription();
  const { address, isGuest, isReanalysis } = location.state || {};

  const getIntelligentDefaults = () => {
    return {
      capRate: '',
      propertyPrice: '',
      monthlyHOA: '100',
      propertyTax: '5000',
      yearlyInsurance: '1200',
      managementFeePercentage: '10',
      yearlyMaintenance: '1200',
      monthlyRent: '',
      vacancyRate: '5',
      additionalExpenses: '0',
      downpaymentPercentage: '100',
      propertyAddress: address
    };
  };

  const [formData, setFormData] = useState({
    capRate: '',
    propertyPrice: '',
    monthlyHOA: '',
    propertyTax: '',
    yearlyInsurance: '',
    managementFeePercentage: '',
    yearlyMaintenance: '',
    monthlyRent: '',
    vacancyRate: '',
    additionalExpenses: '0',
    downpaymentPercentage: '100',
    propertyAddress: address
  });

  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitType, setLimitType] = useState('property_analysis');
  const [recalculateCount, setRecalculateCount] = useState(parseInt(localStorage.getItem('guestRecalculateCount') || '0'));
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savedFormData, setSavedFormData] = useState(null);
  const [showManualAnalysisBanner, setShowManualAnalysisBanner] = useState(false);

  useEffect(() => {
    if (address) {
      loadPropertyData(address);
    } else {
      // For reanalysis, set initial form data as saved
      setSavedFormData({ ...formData });
    }
  }, [address]);

  // Set savedFormData when formData is initially populated
  useEffect(() => {
    if (formData.propertyAddress && !savedFormData) {
      setSavedFormData({ ...formData });
    }
  }, [formData, savedFormData]);

  // Track unsaved changes
  useEffect(() => {
    if (savedFormData) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(savedFormData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, savedFormData]);

  // Prevent navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadPropertyData = async (propertyAddress) => {
    // Check subscription limits before making API call (skip for re-analysis)
    if (user && !isReanalysis && !canPerformAction('property_analysis')) {
      setLimitType('property_analysis');
      setShowLimitModal(true);
      return;
    }

    setLoading(true);
    try {
      const data = await fetchPropertyData(propertyAddress, user?.userId, isReanalysis);
      updateFormFields(data);
      
      // Track usage after successful API call (skip for re-analysis)
      if (user && !isReanalysis) {
        trackPropertyAnalysis();
      }
      
      setNotification({ 
        show: true, 
        message: 'Property data loaded successfully', 
        type: 'success' 
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error loading property data:', error);
      
      // Handle monthly limit gracefully
      if (error.message.includes('Monthly limit reached')) {
        setLimitType('property_analysis');
        setShowLimitModal(true);
      } else {
        setShowManualAnalysisBanner(true);
        // Apply intelligent defaults for manual analysis
        const defaultData = getIntelligentDefaults();
        setFormData(prev => ({
          ...defaultData,
          propertyAddress: propertyAddress // Keep the original address
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSliderChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value.toString()
    });
  };

  const getSliderConfig = (fieldName) => {
    const configs = {
      propertyPrice: { min: 0, max: 2000000, step: 5000 },
      monthlyRent: { min: 0, max: 10000, step: 50 },
      propertyTax: { min: 0, max: 50000, step: 100 },
      yearlyInsurance: { min: 0, max: 10000, step: 50 },
      monthlyHOA: { min: 0, max: 1000, step: 10 },
      managementFeePercentage: { min: 0, max: 20, step: 0.5 },
      vacancyRate: { min: 0, max: 20, step: 0.5 },
      yearlyMaintenance: { min: 0, max: 20000, step: 100 },
      additionalExpenses: { min: 0, max: 10000, step: 25 },
      downpaymentPercentage: { min: 0, max: 100, step: 1 }
    };
    return configs[fieldName] || { min: 0, max: 100, step: 1 };
  };

  const validateRequiredFields = () => {
    const requiredFields = [
      'propertyPrice', 
      'monthlyRent', 
      'propertyTax', 
      'yearlyInsurance', 
      'monthlyHOA', 
      'managementFeePercentage', 
      'yearlyMaintenance', 
      'vacancyRate'
    ];
    const missingFields = [];
    
    requiredFields.forEach(field => {
      const value = formData[field];
      const stringValue = value ? value.toString().trim() : '';
      // For propertyPrice and monthlyRent, 0 is not valid; for others, 0 might be okay
      const isZeroInvalid = (field === 'propertyPrice' || field === 'monthlyRent') && (stringValue === '0' || value === 0);
      const isEmpty = !value || stringValue === '' || isZeroInvalid;
      
      if (isEmpty) {
        missingFields.push(field);
      }
    });
    
    return missingFields;
  };

  const handleSaveChanges = async () => {
    if (!user) {
      setShowAuthPrompt(true);
      return;
    }

    if (!address) {
      setNotification({
        show: true,
        message: 'Property address is required to save changes',
        type: 'error'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
      return;
    }

    // Validate required fields for manual analysis (including when banner was previously shown)
    const missingFields = validateRequiredFields();
    if (missingFields.length > 0) {
      const fieldNames = {
        propertyPrice: 'Property Price',
        monthlyRent: 'Monthly Rent',
        propertyTax: 'Property Tax',
        yearlyInsurance: 'Insurance',
        monthlyHOA: 'HOA',
        managementFeePercentage: 'Management Fee',
        yearlyMaintenance: 'Maintenance',
        vacancyRate: 'Vacancy Rate'
      };
      const missingFieldNames = missingFields.map(field => fieldNames[field]).join(', ');
      setNotification({
        show: true,
        message: `Please fill in all required fields: ${missingFieldNames}`,
        type: 'error'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
      return;
    }

    setLoading(true);
    try {
      // Save user's current form data with calculated metrics
      const currentROI = calculateROI();
      const dataToSave = {
        ...formData,
        capRate: currentROI.capRate,
        cashOnCash: currentROI.cashOnCash,
        netIncome: currentROI.netIncome,
        annualCashFlow: currentROI.annualCashFlow
      };
      await saveUserPropertyData(user.userId, address, dataToSave);
      
      // Mark as saved
      setSavedFormData({ ...formData });
      
      setNotification({
        show: true,
        message: 'Changes saved successfully',
        type: 'success'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error saving changes:', error);
      setNotification({
        show: true,
        message: `Save failed: ${error.message}`,
        type: 'error'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    // Check subscription limits before making API call (skip for re-analysis)
    if (user && !isReanalysis && !canPerformAction('property_analysis')) {
      setLimitType('property_analysis');
      setShowLimitModal(true);
      return;
    }

    // For guest users, allow one recalculate, then show auth prompt
    if (isGuest && !user && recalculateCount >= 1) {
      setShowAuthPrompt(true);
      return;
    }

    setLoading(true);
    try {
      const data = await recalculateROI(formData, user?.userId, isReanalysis);
      updateFormFields(data);
      
      // Track usage after successful API call (skip for re-analysis)
      if (user && !isReanalysis) {
        trackPropertyAnalysis();
      }
      
      // Track guest recalculates
      if (isGuest && !user) {
        const newCount = recalculateCount + 1;
        setRecalculateCount(newCount);
        localStorage.setItem('guestRecalculateCount', newCount.toString());
      }
      
      setNotification({
        show: true,
        message: 'Recalculation successful',
        type: 'success'
      });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error during recalculation:', error);
      
      // Handle monthly limit gracefully
      if (error.message.includes('Monthly limit reached')) {
        setLimitType('property_analysis');
        setShowLimitModal(true);
      } else {
        setNotification({
          show: true,
          message: `Unable to recalculate with updated data. The form calculations shown are still accurate for your analysis.`,
          type: 'info'
        });
        setTimeout(() => setNotification({ show: false, message: '', type: '' }), 6000);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const updateFormFields = (data) => {
    const updatedData = { ...formData };
    
    // Update form fields with responseData
    Object.keys(data).forEach(key => {
      if (formData.hasOwnProperty(key)) {
        updatedData[key] = data[key];
      }
    });
  
    setFormData(updatedData);
    setSavedFormData(updatedData); // Mark as saved after API update
    
    // Update property context for chatbot
    updatePropertyData(updatedData);
    
    // If ROI results are in the data, update those too
    if (data.annualReturnRate || data.capRate || data.monthlyCashFlow) {
      updateRoiResults(data);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    
    // If it's already a string with a dollar sign, return as is
    if (typeof value === 'string' && value.startsWith('$')) return value;
    
    // Otherwise format it
    return `$${parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };
  
  // Calculate simplified ROI metrics (matching backend calculation)
  const calculateROI = () => {
    const price = parseFloat(formData.propertyPrice) || 0;
    if (price === 0) return { capRate: '0.0', cashOnCash: '0.0', netIncome: '0' };
    
    const monthlyRent = parseFloat(formData.monthlyRent) || 0;
    const vacancyRate = parseFloat(formData.vacancyRate) || 0;
    const managementFeePercentage = parseFloat(formData.managementFeePercentage) || 0;
    const monthlyHOA = parseFloat(formData.monthlyHOA) || 0;
    const yearlyTax = parseFloat(formData.propertyTax) || 0;
    const yearlyInsurance = parseFloat(formData.yearlyInsurance) || 0;
    const yearlyMaintenance = parseFloat(formData.yearlyMaintenance) || 0;
    const additionalExpenses = (parseFloat(formData.additionalExpenses) || 0) * 12;
    
    // Match backend calculation exactly
    const grossIncome = monthlyRent * 12 - (monthlyRent / 30) * (vacancyRate / 100) * 365;
    const totalExpense = yearlyTax + monthlyHOA * 12 + yearlyInsurance + (managementFeePercentage / 100) * monthlyRent * 12 + yearlyMaintenance;
    const netIncome = grossIncome - totalExpense;
    
    // Cap Rate (matching backend: round to 1 decimal place)
    const capRate = (netIncome / price) * 100;
    
    // Cash on Cash Return (after all expenses including debt service)
    const downPaymentPercent = parseFloat(formData.downpaymentPercentage) / 100 || 1;
    const downPayment = price * downPaymentPercent;
    const annualCashFlow = netIncome - additionalExpenses;
    const cashOnCash = downPayment > 0 ? (annualCashFlow / downPayment) * 100 : 0;
    
    console.log('=== FORM PAGE DEBUG ===');
    console.log('Input values:', {
      price, monthlyRent, vacancyRate, managementFeePercentage, 
      yearlyTax, monthlyHOA, yearlyInsurance, yearlyMaintenance, additionalExpenses
    });
    console.log('Calculations:', {
      grossIncome, totalExpense, netIncome, annualCashFlow, downPayment, 
      cashOnCash, cashOnCashFormatted: cashOnCash.toFixed(2)
    });
    
    return {
      capRate: Math.round(capRate * 10) / 10, // Round to 1 decimal place like backend
      cashOnCash: Math.round(cashOnCash * 10) / 10, // Round to 1 decimal place for consistency
      netIncome: Math.round(netIncome * 10) / 10, // Round to 1 decimal place like backend
      annualCashFlow: Math.round(annualCashFlow * 10) / 10 // Round to 1 decimal place like backend
    };
  };
  
  const roiMetrics = calculateROI();
  
  return (
    <div className="property-form-page">
      {notification.show && (
        <Notification 
          message={notification.message} 
          type={notification.type} 
        />
      )}
      
      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1>Property Analysis</h1>
          <h2 className="property-address">{address || "Property Details"}</h2>
          
          {isGuest && !user && (
            <div className="guest-banner">
              <p>🎯 You're viewing as a guest. {process.env.REACT_APP_LOGIN_ONLY_MODE !== 'true' && <><a href="/signup">Sign up</a> or </>}<a href="/login">log in</a> to save your analysis and unlock full features!</p>
            </div>
          )}
        </div>
      </div>

      {/* Manual Analysis Banner */}
      {showManualAnalysisBanner && (
        <div className="manual-analysis-banner">
          <div className="banner-content">
            <div className="banner-icon">💡</div>
            <div className="banner-text">
              <div className="banner-title">Manual Analysis Mode</div>
              <div className="banner-message">
                Property data couldn't be retrieved automatically. Please enter the required fields (Property Price* and Monthly Rent*) and review the intelligent defaults below. All fields with values are required for accurate calculations.
              </div>
            </div>
            <button 
              className="banner-close"
              onClick={() => setShowManualAnalysisBanner(false)}
              aria-label="Close banner"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Main Content Container */}
      <div className="main-content">
        
        {/* ROI Metrics Card */}
        <div className="content-card">
          <div className="card-header">
            <h3 className="card-title">Key Metrics</h3>
            {address && (
              <PDFExportButton 
                propertyData={formData}
                calculatedMetrics={roiMetrics}
                address={address}
                className="btn-secondary"
                includeAdvanced={true}
              />
            )}
          </div>
          <div className={`roi-metrics ${loading ? 'loading' : ''}`}>
            {loading && (
              <div className="loading-overlay">
                <Spinner size="medium" message="Calculating ROI..." />
              </div>
            )}
            <div className="metric">
              <span className="metric-value">{roiMetrics.capRate}%</span>
              <span className="metric-label">Cap Rate</span>
            </div>
            <div className="metric">
              <span className="metric-value">{roiMetrics.cashOnCash}%</span>
              <span className="metric-label">Cash on Cash</span>
            </div>
            <div className="metric">
              <span className="metric-value">{formatCurrency(roiMetrics.netIncome)}</span>
              <span className="metric-label">Annual NOI</span>
            </div>
          </div>
        </div>

        {/* Advanced ROI Metrics */}
        <AdvancedROIMetrics formData={formData} />

        {/* Property Details Form */}
        <div className="content-card">
          <h3 className="card-title">Property Details</h3>
          <form className="property-form">
            <div className="form-columns">
              <div className="form-column">
                <div className="form-group">
                  <label htmlFor="propertyPrice">
                    Purchase Price {showManualAnalysisBanner && <span className="required">*</span>}
                  </label>
                  <input 
                    type="text" 
                    id="propertyPrice"
                    name="propertyPrice" 
                    value={formData.propertyPrice} 
                    onChange={handleChange} 
                    className={`form-control ${showManualAnalysisBanner && !formData.propertyPrice ? 'required-field' : ''}`}
                    placeholder={showManualAnalysisBanner ? "Required - Enter property price" : ""}
                  />
                  <input
                    type="range"
                    min={getSliderConfig('propertyPrice').min}
                    max={getSliderConfig('propertyPrice').max}
                    step={getSliderConfig('propertyPrice').step}
                    value={parseFloat(formData.propertyPrice) || 0}
                    onChange={(e) => handleSliderChange('propertyPrice', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$2M</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="monthlyRent">
                    Monthly Rent {showManualAnalysisBanner && <span className="required">*</span>}
                  </label>
                  <input 
                    type="text" 
                    id="monthlyRent"
                    name="monthlyRent" 
                    value={formData.monthlyRent} 
                    onChange={handleChange}
                    className={`form-control ${showManualAnalysisBanner && !formData.monthlyRent ? 'required-field' : ''}`}
                    placeholder={showManualAnalysisBanner ? "Required - Enter monthly rent" : ""}
                  />
                  <input
                    type="range"
                    min={getSliderConfig('monthlyRent').min}
                    max={getSliderConfig('monthlyRent').max}
                    step={getSliderConfig('monthlyRent').step}
                    value={parseFloat(formData.monthlyRent) || 0}
                    onChange={(e) => handleSliderChange('monthlyRent', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$10K</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="propertyTax">
                    Annual Property Tax {showManualAnalysisBanner && <span className="required">*</span>}
                    {showManualAnalysisBanner && formData.propertyTax && <span className="default-value">(Default: $5,000)</span>}
                  </label>
                  <input 
                    type="text" 
                    id="propertyTax"
                    name="propertyTax" 
                    value={formData.propertyTax} 
                    onChange={handleChange}
                    className={`form-control ${showManualAnalysisBanner && !formData.propertyTax ? 'required-field' : ''}`}
                  />
                  <input
                    type="range"
                    min={getSliderConfig('propertyTax').min}
                    max={getSliderConfig('propertyTax').max}
                    step={getSliderConfig('propertyTax').step}
                    value={parseFloat(formData.propertyTax) || 0}
                    onChange={(e) => handleSliderChange('propertyTax', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$50K</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="yearlyInsurance">
                    Annual Insurance {showManualAnalysisBanner && <span className="required">*</span>}
                    {showManualAnalysisBanner && formData.yearlyInsurance && <span className="default-value">(Default: $1,200)</span>}
                  </label>
                  <input 
                    type="text" 
                    id="yearlyInsurance"
                    name="yearlyInsurance" 
                    value={formData.yearlyInsurance} 
                    onChange={handleChange}
                    className={`form-control ${showManualAnalysisBanner && !formData.yearlyInsurance ? 'required-field' : ''}`}
                  />
                  <input
                    type="range"
                    min={getSliderConfig('yearlyInsurance').min}
                    max={getSliderConfig('yearlyInsurance').max}
                    step={getSliderConfig('yearlyInsurance').step}
                    value={parseFloat(formData.yearlyInsurance) || 0}
                    onChange={(e) => handleSliderChange('yearlyInsurance', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$10K</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="downpaymentPercentage">Downpayment Percentage (%)</label>
                  <input 
                    type="text" 
                    id="downpaymentPercentage"
                    name="downpaymentPercentage" 
                    value={formData.downpaymentPercentage} 
                    onChange={handleChange}
                    className="form-control" 
                  />
                  <input
                    type="range"
                    min={getSliderConfig('downpaymentPercentage').min}
                    max={getSliderConfig('downpaymentPercentage').max}
                    step={getSliderConfig('downpaymentPercentage').step}
                    value={parseFloat(formData.downpaymentPercentage) || 0}
                    onChange={(e) => handleSliderChange('downpaymentPercentage', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
              
              <div className="form-column">
                <div className="form-group">
                  <label htmlFor="monthlyHOA">
                    Monthly HOA {showManualAnalysisBanner && <span className="required">*</span>}
                    {showManualAnalysisBanner && formData.monthlyHOA && <span className="default-value">(Default: $100)</span>}
                  </label>
                  <input 
                    type="text" 
                    id="monthlyHOA"
                    name="monthlyHOA" 
                    value={formData.monthlyHOA} 
                    onChange={handleChange}
                    className={`form-control ${showManualAnalysisBanner && !formData.monthlyHOA ? 'required-field' : ''}`}
                  />
                  <input
                    type="range"
                    min={getSliderConfig('monthlyHOA').min}
                    max={getSliderConfig('monthlyHOA').max}
                    step={getSliderConfig('monthlyHOA').step}
                    value={parseFloat(formData.monthlyHOA) || 0}
                    onChange={(e) => handleSliderChange('monthlyHOA', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$1K</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="managementFeePercentage">
                    Management Fee (%) {showManualAnalysisBanner && <span className="required">*</span>}
                    {showManualAnalysisBanner && formData.managementFeePercentage && <span className="default-value">(Default: 10%)</span>}
                  </label>
                  <input 
                    type="text" 
                    id="managementFeePercentage"
                    name="managementFeePercentage" 
                    value={formData.managementFeePercentage} 
                    onChange={handleChange}
                    className={`form-control ${showManualAnalysisBanner && !formData.managementFeePercentage ? 'required-field' : ''}`}
                  />
                  <input
                    type="range"
                    min={getSliderConfig('managementFeePercentage').min}
                    max={getSliderConfig('managementFeePercentage').max}
                    step={getSliderConfig('managementFeePercentage').step}
                    value={parseFloat(formData.managementFeePercentage) || 0}
                    onChange={(e) => handleSliderChange('managementFeePercentage', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>0%</span>
                    <span>20%</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="vacancyRate">
                    Vacancy Rate (%) {showManualAnalysisBanner && <span className="required">*</span>}
                    {showManualAnalysisBanner && formData.vacancyRate && <span className="default-value">(Default: 5%)</span>}
                  </label>
                  <input 
                    type="text" 
                    id="vacancyRate"
                    name="vacancyRate" 
                    value={formData.vacancyRate} 
                    onChange={handleChange}
                    className={`form-control ${showManualAnalysisBanner && !formData.vacancyRate ? 'required-field' : ''}`}
                  />
                  <input
                    type="range"
                    min={getSliderConfig('vacancyRate').min}
                    max={getSliderConfig('vacancyRate').max}
                    step={getSliderConfig('vacancyRate').step}
                    value={parseFloat(formData.vacancyRate) || 0}
                    onChange={(e) => handleSliderChange('vacancyRate', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>0%</span>
                    <span>20%</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="yearlyMaintenance">
                    Annual Maintenance {showManualAnalysisBanner && <span className="required">*</span>}
                    {showManualAnalysisBanner && formData.yearlyMaintenance && <span className="default-value">(Default: $1,200)</span>}
                  </label>
                  <input 
                    type="text" 
                    id="yearlyMaintenance"
                    name="yearlyMaintenance" 
                    value={formData.yearlyMaintenance} 
                    onChange={handleChange}
                    className={`form-control ${showManualAnalysisBanner && !formData.yearlyMaintenance ? 'required-field' : ''}`}
                  />
                  <input
                    type="range"
                    min={getSliderConfig('yearlyMaintenance').min}
                    max={getSliderConfig('yearlyMaintenance').max}
                    step={getSliderConfig('yearlyMaintenance').step}
                    value={parseFloat(formData.yearlyMaintenance) || 0}
                    onChange={(e) => handleSliderChange('yearlyMaintenance', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$20K</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="additionalExpenses">Additional Monthly Expense (e.g. monthly mortgage)</label>
                  <input 
                    type="text" 
                    id="additionalExpenses"
                    name="additionalExpenses" 
                    value={formData.additionalExpenses} 
                    onChange={handleChange}
                    className="form-control" 
                  />
                  <input
                    type="range"
                    min={getSliderConfig('additionalExpenses').min}
                    max={getSliderConfig('additionalExpenses').max}
                    step={getSliderConfig('additionalExpenses').step}
                    value={parseFloat(formData.additionalExpenses) || 0}
                    onChange={(e) => handleSliderChange('additionalExpenses', e.target.value)}
                    className="form-slider"
                  />
                  <div className="slider-labels">
                    <span>$0</span>
                    <span>$10K</span>
                  </div>
                </div>
              </div>
            </div>
            
            
            {hasUnsavedChanges && (
              <div className="unsaved-changes-banner">
                <span className="unsaved-indicator">⚠️ You have unsaved changes</span>
                <button 
                  type="button"
                  onClick={handleSaveChanges} 
                  className="btn-save-changes"
                  disabled={loading}
                >
                  Save Changes
                </button>
              </div>
            )}
            

            {showAuthPrompt && (
              <div className="auth-prompt-modal">
                <div className="auth-prompt-content">
                  <h3>Sign Up to Continue</h3>
                  <p>To recalculate ROI and access full features, please create an account or log in.</p>
                  <div className="auth-prompt-actions">
                    {process.env.REACT_APP_LOGIN_ONLY_MODE !== 'true' && (
                      <button onClick={() => navigate('/signup')} className="btn-primary">Sign Up</button>
                    )}
                    <button onClick={() => navigate('/login')} className="btn-secondary">Log In</button>
                    <button onClick={() => setShowAuthPrompt(false)} className="btn-link">Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Property Comparison */}
        <PropertyComparison currentProperty={formData} />

      </div>
      
      {/* Limit Reached Modal */}
      <LimitReachedModal 
        show={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        limitType={limitType}
      />
    </div>
  );
};

export default FormPage;
